import React, { ReactNode } from 'react';
import { startCase, isNil } from 'lodash';
import styled from 'styled-components';
import { LinearBuffer } from '../components/Progress';
import * as Types from 'types';
import { Tooltip, Typography, Grid, Chip, Box } from '@mui/material';
import { AlertProps } from '@mui/material';
import { StyledAlert } from 'shared/styles';
import * as IntegrationTypes from 'IntegrationModule/types';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import LockIcon from '@mui/icons-material/Lock';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import isEmpty from 'lodash/isEmpty';

// Consistent icon container for all integration health indicators
const IntegrationHealthIconWrapper = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.7rem;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &.sidebar {
    width: 1.125rem;
    height: 1.125rem;

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }

  &.success svg {
    color: #4caf50; // success green
  }

  &.info svg {
    color: #2196f3; // info blue
  }

  &.warning svg {
    color: #ff9800; // warning orange
  }

  &.error svg {
    color: #f44336; // error red
  }

  &.disabled svg {
    color: #a0a0a0; // disabled gray
  }
`;

const BrokenLinkIcon = styled(LinkOffIcon)`
  color: #a0a0a0;
  font-size: 1.25rem; // 20px - consistent with MUI Alert icon size
  margin-right: 0.7rem;
  display: flex;
  align-items: center;
  flex-shrink: 0; // Prevent icon from shrinking
  width: 1.25rem; // Fixed width for consistency
  height: 1.25rem; // Fixed height for consistency

  &.sidebar {
    font-size: 1.125rem; // 18px - match sidebar text size
    margin: 0;
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const StyledListItem = styled.li`
  max-width: 25rem;
  word-break: break-word;
`;

/**
 * Green (success) = if no create or update
 * Blue (info) = any update and no create
 * Yellow (warning) = More than 30 and less than 100 inclusive (create can be very little, but total create + total update can be very big)
 * Red (error) = Less than 30%
 * Note: Refer to Material UI component <Alert> in "@mui/material"
 * @param {IntegrationTypes.CountPerDataTypeValues} countsByDataType
 * @param {number} ratioPercentage
 * @returns AlertProps["color"] | string
 */
export const getSyncRatioAlertDisplay = (
  countsByDataType: IntegrationTypes.CountPerDataTypeValues,
  ratioPercentage: number,
): AlertProps['color'] | string => {
  const { totalCreate, totalUpdate } = countsByDataType;

  if (!totalCreate && !totalUpdate) return 'success';

  if (!totalCreate && totalUpdate) return 'info';

  if (ratioPercentage > 30 && ratioPercentage <= 100) return 'warning';

  if (ratioPercentage <= 30) return 'error';
};

/**
 * Success / Request ratio
 */
export const getIntegrationHealthAlertDisplay = (percentageNumber: number) => {
  if (percentageNumber <= 50) return 'error';

  if (percentageNumber <= 70) return 'warning';

  if (percentageNumber <= 95) return 'info';

  return 'success';
};

export const calculateAlertSeverityByStatusCode = (statusCode: number) => {
  if (statusCode <= 299) return 'success';

  if (statusCode <= 399) return 'info';

  if (statusCode <= 499) return 'warning';

  if (statusCode >= 500) return 'error';

  return 'success';
};

const renderSubRowArrayRecursive = (arr: Array<any>): JSX.Element => {
  return (
    <>
      {arr.map((v, index) => {
        // Generate stable key from content hash or use index as last resort
        // For static display content that won't be reordered, index is acceptable
        const keyPrefix = `subrow-${index}`;

        if (typeof v === 'object' && v !== null) {
          return <div key={`${keyPrefix}-obj`}>{renderSubRowRecursive(v)}</div>;
        }

        if (Array.isArray(v))
          return <div key={`${keyPrefix}-arr`}>{renderSubRowArrayRecursive(v)}</div>;

        return <div key={`${keyPrefix}-${String(v).slice(0, 20)}`}>{v}</div>;
      })}
    </>
  );
};

export const renderSubRowRecursive = (records: Record<any, any> | any[]) => {
  if (!records) return;

  if (Array.isArray(records)) {
    return <ul>{renderSubRowArrayRecursive(records)}</ul>;
  }

  if (typeof records !== 'object') {
    return JSON.stringify(records);
  }

  return (
    <ul>
      {Object.entries(records).map(([key, value]) => {
        if (Array.isArray(value)) {
          return (
            <StyledListItem key={key}>
              <b>{startCase(key)}</b>: {renderSubRowArrayRecursive(value)}
            </StyledListItem>
          );
        }

        if (typeof value === 'object' && value !== null) {
          return (
            <React.Fragment key={key}>
              <StyledListItem>
                <b>{startCase(key)}</b>
              </StyledListItem>

              {renderSubRowRecursive(value)}
            </React.Fragment>
          );
        }

        return (
          <StyledListItem key={key}>
            <b>{startCase(key)}</b>: {'  '}
            {stringifyIfNotUndefinedNorNull(value)}
          </StyledListItem>
        );
      })}
    </ul>
  );
};

export const stringifyIfNotUndefinedNorNull = (value: any, falseReturnValue = 'N/A') => {
  return value !== null && value !== undefined ? String(value) : falseReturnValue;
};

type ConditionallyRenderElement = {
  isLoading: boolean;
  /**
   *  Make sure not to pass Record (object) type
   */
  error?: ReactNode;
  renderError?: () => unknown;
  content: any;
  Loader?: any;
  loaderCardContentClassName?: string;
};

export const conditionallyRenderElement = ({
  isLoading,
  error,
  renderError,
  content,
  Loader,
  loaderCardContentClassName,
}: ConditionallyRenderElement) => {
  if (isLoading) {
    return Loader || <LinearBuffer loaderCardContentClassName={loaderCardContentClassName} />;
  }

  if (error) {
    return renderError ? renderError() : error;
  }

  return content;
};

const noop = () => {};

/**
 * A util only used for showing 2 icons depending on the health status
 * - Green check mark = If request = success
 * - Red mark = If request > success
 */
export const renderIntegrationHealthStatus = ({
  isHealthy,
  integrationHealth,
  className = '',
  style = {},
  overrideTooltip,
  onClick,
}: {
  isHealthy: boolean | null | undefined;
  integrationHealth: Types.IntegrationHealth;
  className?: string;
  style?: React.CSSProperties;
  overrideTooltip?: any;
  onClick?: (e: any) => void;
}) => {
  return !isNil(isHealthy) ? (
    <Tooltip
      title={
        overrideTooltip || (
          <>
            {/* <Typography>
              Request Count: {integrationHealth.requestCount}
            </Typography> */}
            <Typography>Healthy Connection Today: {integrationHealth.successCount}</Typography>
          </>
        )
      }
    >
      <span>
        <StyledAlert
          onClick={onClick}
          style={style}
          className={className}
          severity={isHealthy ? 'success' : 'error'}
        />
      </span>
    </Tooltip>
  ) : (
    <BrokenLinkIcon />
  );
};

/**
 * Determine the type of integration health issue based on errors
 */
const detectIntegrationIssueType = (
  errors?: Types.IntegrationHealth['errors'],
): 'auth' | 'rateLimit' | 'connection' | 'data' | 'unknown' | null => {
  if (!errors) return null;

  const errorEntries = Object.entries(errors);
  if (errorEntries.length === 0) return null;

  // Check for authentication/authorization errors (highest priority)
  if (errors.AuthorizationError && errors.AuthorizationError > 0) {
    return 'auth';
  }

  // Check for rate limiting (often shows as InvalidParamsError or specific error codes)
  // Note: This is a heuristic - actual rate limiting detection may need backend support
  const totalErrors = errorEntries.reduce((sum, [_, count]) => sum + (count || 0), 0);
  if (errors.InvalidParamsError && errors.InvalidParamsError > totalErrors * 0.5) {
    return 'rateLimit';
  }

  // Connection errors
  if (errors.ConnectionError && errors.ConnectionError > 0) {
    return 'connection';
  }

  // Data errors
  if (errors.DataError && errors.DataError > 0) {
    return 'data';
  }

  // Unknown errors
  if (errors.UnknownError && errors.UnknownError > 0) {
    return 'unknown';
  }

  return null;
};

/**
 * Show integration health icon with comprehensive states and tooltips
 *
 * Icon States:
 * - Not Configured (LinkOffIcon, gray): No integration health data available
 * - Inactive (PauseCircleIcon, gray): Integration exists but no API calls today (requestCount === 0)
 * - Auth Failed (LockIcon, red): Authorization errors detected AND success rate < 95%
 * - Rate Limited (TimerOffIcon, orange): Rate limiting detected
 * - Critical (ErrorIcon, red): Success rate < 50%
 * - Degraded (WarningIcon, orange): Success rate 50-79%
 * - Good (InfoIcon, blue): Success rate 80-94%
 * - Healthy (CheckCircleIcon, green): Success rate 95-100%
 */
export const renderIntegrationHealthByRequestAndSuccessCountPercent = ({
  integrationHealth,
  onClick,
  className,
  style,
  overrideTooltip,
  linkIconClassName = '',
}: {
  integrationHealth?: Types.IntegrationHealth;
  className?: string;
  style?: React.CSSProperties;
  overrideTooltip?: any;
  onClick?: (e: any) => void;
  linkIconClassName?: string;
}) => {
  /**
   * Case 1: No integration health data
   * This means the integration is not configured or data is unavailable
   */
  if (!integrationHealth || isEmpty(integrationHealth)) {
    const tooltip = (
      <Box>
        <Typography variant="body2" fontWeight={600}>
          Not Configured
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          No integration health data available
        </Typography>
      </Box>
    );

    return (
      <Tooltip title={overrideTooltip || tooltip}>
        <span>
          <IntegrationHealthIconWrapper className={`disabled ${linkIconClassName}`}>
            <LinkOffIcon />
          </IntegrationHealthIconWrapper>
        </span>
      </Tooltip>
    );
  }

  /**
   * Case 2: Integration exists but no API calls today (requestCount === 0)
   * This is normal - integration is configured but hasn't been used yet today
   */
  if (integrationHealth.requestCount === 0) {
    const tooltip = (
      <Box>
        <Typography variant="body2" fontWeight={600}>
          Inactive Today
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          Integration configured but no API calls made today
        </Typography>
      </Box>
    );

    return (
      <Tooltip title={overrideTooltip || tooltip}>
        <span>
          <IntegrationHealthIconWrapper className={`disabled ${linkIconClassName}`}>
            <PauseCircleIcon />
          </IntegrationHealthIconWrapper>
        </span>
      </Tooltip>
    );
  }

  const { requestCount, successCount, errors } = integrationHealth;

  const successToRequestRatioPercent = ((successCount / requestCount) * 100).toFixed(1);
  const successRate = +successToRequestRatioPercent;

  /**
   * Case 3: Check for specific error types that require special icons
   */
  const issueType = detectIntegrationIssueType(errors);

  // Authentication/Authorization failures - only show lock when success rate drops below 95%
  // Transient auth errors (e.g. target-side retries) at high success rates are normal
  if (issueType === 'auth' && successRate < 95) {
    const totalErrors = errors
      ? Object.values(errors).reduce((sum, count) => sum + (count || 0), 0)
      : 0;
    const errorRate = ((totalErrors / requestCount) * 100).toFixed(1);

    const tooltip = (
      <Box>
        <Typography variant="body2" fontWeight={600} color="error">
          Authentication Failed
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {successCount} of {requestCount} requests succeeded ({successRate}%)
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'error.main' }}>
          Authorization errors detected ({errorRate}% of requests)
        </Typography>
        {errors?.AuthorizationError && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            • AuthorizationError: {errors.AuthorizationError}
          </Typography>
        )}
        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
          Check integration credentials and permissions
        </Typography>
      </Box>
    );

    return (
      <Tooltip title={overrideTooltip || tooltip}>
        <span>
          <IntegrationHealthIconWrapper
            className={`error ${className || ''}`}
            onClick={onClick}
            style={{ ...style, cursor: onClick ? 'pointer' : 'default' }}
          >
            <LockIcon />
          </IntegrationHealthIconWrapper>
        </span>
      </Tooltip>
    );
  }

  // Rate limiting detected
  if (issueType === 'rateLimit') {
    const tooltip = (
      <Box>
        <Typography variant="body2" fontWeight={600} color="warning.main">
          Rate Limited
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {successCount} of {requestCount} requests succeeded ({successRate}%)
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          Rate limiting detected - requests being throttled
        </Typography>
        {errors?.InvalidParamsError && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            • InvalidParamsError: {errors.InvalidParamsError}
          </Typography>
        )}
        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
          Consider reducing request frequency or upgrading API plan
        </Typography>
      </Box>
    );

    return (
      <Tooltip title={overrideTooltip || tooltip}>
        <span>
          <IntegrationHealthIconWrapper
            className={`warning ${className || ''}`}
            onClick={onClick}
            style={{ ...style, cursor: onClick ? 'pointer' : 'default' }}
          >
            <TimerOffIcon />
          </IntegrationHealthIconWrapper>
        </span>
      </Tooltip>
    );
  }

  /**
   * Case 4: Standard health status based on success rate
   */
  const formatError = ({ errorName, errorCount }: { errorName: string; errorCount: number }) => {
    const errorPercentage = ((errorCount / requestCount) * 100).toFixed(1);
    return (
      <Typography key={errorName} variant="caption" display="block" sx={{ mt: 0.3 }}>
        • {errorName}: {errorCount} ({errorPercentage}%)
      </Typography>
    );
  };

  // Determine status label and severity
  let statusLabel = 'Healthy';
  let statusDescription = 'All systems operating normally';
  if (successRate < 50) {
    statusLabel = 'Critical';
    statusDescription = 'Immediate attention required';
  } else if (successRate < 80) {
    statusLabel = 'Degraded';
    statusDescription = 'Performance issues detected';
  } else if (successRate < 95) {
    statusLabel = 'Good';
    statusDescription = 'Minor issues detected';
  }

  const integrationHealthTooltip = (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {statusLabel} ({successRate}%)
      </Typography>
      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
        {successCount} of {requestCount} requests succeeded today
      </Typography>
      {successRate < 100 && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
          {statusDescription}
        </Typography>
      )}
      {errors && Object.entries(errors).some(([_, count]) => count > 0) && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" display="block" fontWeight={600}>
            Error Breakdown:
          </Typography>
          {Object.entries(errors).map(([errorName, errorCount]) =>
            errorCount ? formatError({ errorName, errorCount }) : null,
          )}
        </Box>
      )}
      {onClick && (
        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
          Click to view details
        </Typography>
      )}
    </Box>
  );

  const severity = getIntegrationHealthAlertDisplay(successRate);

  // Map severity to icon component
  const getIconForSeverity = (severity: string) => {
    switch (severity) {
      case 'success':
        return CheckCircleIcon;
      case 'info':
        return InfoIcon;
      case 'warning':
        return WarningIcon;
      case 'error':
        return ErrorIcon;
      default:
        return CheckCircleIcon;
    }
  };

  const IconComponent = getIconForSeverity(severity);

  return (
    <Tooltip title={overrideTooltip || integrationHealthTooltip}>
      <span>
        <IntegrationHealthIconWrapper
          className={`${severity} ${className || ''}`}
          onClick={onClick}
          style={{ ...style, cursor: onClick ? 'pointer' : 'default' }}
        >
          <IconComponent />
        </IntegrationHealthIconWrapper>
      </span>
    </Tooltip>
  );
};

const renderRemainingChips = (tags: Array<string>, limit = 3) => {
  return tags && tags.length > limit ? (
    <ChipContainer className="tooltip-chip-container">
      <Grid>
        {tags.slice(limit).map((tag) => (
          <Chip key={tag} className="tooltip-chip" label={tag} color="primary" />
        ))}
      </Grid>
    </ChipContainer>
  ) : (
    <></>
  );
};

const renderVisibleChips = (tags: Array<string>, limit: number) => {
  return (
    <ChipContainer>
      {tags.slice(0, limit).map((tag) => (
        <Chip key={tag} className="chip" label={tag} color="primary" />
      ))}
      {tags.length >= limit + 1 && <Chip className="chip" label={`+${tags.slice(limit).length}`} />}
    </ChipContainer>
  );
};

export const renderChips = (chips: Array<string>, limit: number) => {
  return (
    <Tooltip placement="top-start" title={renderRemainingChips(chips || [], limit)}>
      <span>{renderVisibleChips(chips || [], limit)}</span>
    </Tooltip>
  );
};

/* -------------------------------------------------------------------------- */

const ChipContainer = styled.span`
  .MuiChip-root {
    margin-right: 15px;
    margin-top: 10px;
    margin-bottom: 15px;
    height: 26px;
  }
  display: flex;
  justify-content: flex-start;
  &.tooltip-chip-container {
    flex-direction: column;
  }
  .chip {
    margin: 0 3px;
    height: 23px;
  }
  .tooltip-chip {
    margin: 4px;
    max-width: 280px;
  }
`;
